import { toast } from "sonner";

import {
  FilterEmpty,
  InputKeyLimiterProps,
  InputPasteLimiterProps,
  ValidateResponseProps,
} from "./helper-function.types";

import { DEFAULT_MAX_CHARS, DEFAULT_MAX_VALUE } from "@/constants/constants";
import { TAny } from "@/interfaces/globals";

/**
 * Prevents input that would exceed specified limits
 * @param {InputKeyLimiterProps} props object
 * @returns {void}
 */
export function InputKeyLimiter(props: InputKeyLimiterProps): void {
  const {
    event,
    allowNegative,
    maxChars,
    maxValue,
    allowDecimal = true,
  } = props;
  const { value, selectionStart } = event.currentTarget;
  const { key } = event;
  //this is for copy paste
  if (event.ctrlKey || event.metaKey) {
    return; // Allow Ctrl+V (Windows) or Cmd+V (Mac)
  }
  const preventKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"];

  if (preventKeys.includes(key)) {
    // Allow essential control keys (navigation & editing)
    return;
  }

  // Prevent exceeding max character length for both text & number inputs
  if (maxChars && value.length >= maxChars) {
    event.preventDefault();

    return;
  }
  // Handle number inputs separately
  if (maxValue) {
    if (!/^[0-9.]$/.test(event.key)) {
      event.preventDefault();

      return;
    }
    if (key === "ArrowUp" && +value === maxValue) {
      event.preventDefault();

      return;
    }

    if (key === "." && !allowDecimal) {
      event.preventDefault();

      return;
    }

    // Prevent multiple decimal points
    if (key === "." && value.includes(".") && allowDecimal) {
      event.preventDefault();

      return;
    }

    // Handle negative sign
    if (key === "-") {
      // Only allow negative if enabled and only at the beginning
      if (
        !allowNegative ||
        (selectionStart !== 0 && selectionStart !== null) ||
        value.includes("-")
      ) {
        event.preventDefault();
      }

      return;
    }

    // Form new potential value
    const newValue = (value + key).replace(/^0+/, "0"); // Remove leading zeros to prevent issues

    // Ensure the number is within the limit
    if (!isNaN(Number(newValue)) && Math.abs(Number(newValue)) > maxValue) {
      event.preventDefault();
    }
  }
}

/**
 *
 * @param {InputPasteLimiterProps} props object
 * @returns {void}
 */
export function InputPasteLimiter(props: InputPasteLimiterProps): void {
  const {
    event,
    allowNegative,
    maxChars,
    maxValue,
    allowDecimal = true,
  } = props;

  const { value, selectionStart, selectionEnd } = event.currentTarget;
  const clipboardData = event.clipboardData.getData("text");

  // If no clipboard data, allow the paste
  if (!clipboardData) {
    return;
  }

  // Calculate the new value after paste
  const beforeSelection = value.substring(0, selectionStart || 0);
  const afterSelection = value.substring(selectionEnd || selectionStart || 0);
  const newValue = beforeSelection + clipboardData + afterSelection;

  // Check max character length
  if (maxChars && newValue.length > maxChars) {
    event.preventDefault();

    return;
  }

  // Handle number inputs separately
  if (maxValue) {
    // Check if pasted content contains only valid number characters
    const validNumberRegex = allowNegative ? /^-?[0-9.]*$/ : /^[0-9.]*$/;

    if (!validNumberRegex.test(clipboardData)) {
      event.preventDefault();

      return;
    }

    // Check decimal restrictions
    if (!allowDecimal && clipboardData.includes(".")) {
      event.preventDefault();

      return;
    }

    // Check for multiple decimal points in the final value
    if (allowDecimal && (newValue.match(/\./g) || []).length > 1) {
      event.preventDefault();

      return;
    }

    // Check negative sign restrictions
    if (clipboardData.includes("-")) {
      // Check if negative is allowed
      if (!allowNegative) {
        event.preventDefault();

        return;
      }

      // Check if negative sign is in correct position (beginning)
      const negativeIndex = clipboardData.indexOf("-");
      if (
        negativeIndex !== 0 ||
        (selectionStart !== 0 && selectionStart !== null)
      ) {
        event.preventDefault();

        return;
      }

      // Check if there's already a negative sign in the value
      if (value.includes("-")) {
        event.preventDefault();

        return;
      }

      // Check for multiple negative signs in pasted content
      if ((clipboardData.match(/-/g) || []).length > 1) {
        event.preventDefault();

        return;
      }
    }

    // Remove leading zeros for validation (but preserve single zero)
    const cleanedNewValue = newValue.replace(/^0+/, "") || "0";
    const finalValue = cleanedNewValue.startsWith(".")
      ? "0" + cleanedNewValue
      : cleanedNewValue;

    // Check if the final number exceeds the maximum value
    if (!isNaN(Number(finalValue)) && Math.abs(Number(finalValue)) > maxValue) {
      event.preventDefault();

      return;
    }
  }
}

/**
 * Prevents pasting values that exceed the specified limits
 * @param {React.ClipboardEvent<HTMLInputElement>} event - Paste event
 * @param {number} maxValue - The maximum numeric value allowed
 * @param {number} maxChars - The maximum character length allowed
 * @returns {void}
 */
export function handlePaste(
  event: React.ClipboardEvent<HTMLInputElement>,
  maxValue: number = DEFAULT_MAX_VALUE,
  maxChars: number = DEFAULT_MAX_CHARS,
): void {
  const pasteData = event.clipboardData.getData("text");

  // Prevent pasting if it exceeds max characters
  if (pasteData.length > maxChars) {
    event.preventDefault();

    //Show error message
    toast.error(`Maximum characters limit exceed allowed is ${maxChars}`);

    return;
  }

  // If it's a number input, ensure it doesn't exceed max value
  const numericValue = Number(pasteData);
  if (!isNaN(numericValue) && numericValue > maxValue) {
    //Show error message
    toast.error(`Maximum value exceed allowed is ${maxValue}`);

    event.preventDefault();
  }
}

/**
 * @param {object} data Object to be converted to FormData
 * @param {string[]} imgKey Array of keys that are images
 * @param {object} dirtyFields Object of dirty fields to be sent. If you want to send all fields, pass an empty object
 * @returns {FormData} - The FormData object
 * @example
 * const data = {
 *  name: "John Doe",
 *  image: [File]
 * }
 *
 * const formData = parseToFormData(data, ["image"])
 */
export const parseToFormData = (
  data: {
    [key: string]: TAny;
  },
  imgKey?: string[],
  // dirtyFields?: Record<string, boolean | Record<string, boolean>>,

  dirtyFields?: object,
): FormData => {
  const formData = new FormData();

  Object.entries(data).forEach(([key, value]) => {
    if (
      dirtyFields &&
      Object.keys(dirtyFields).length > 0 &&
      !dirtyFields[key as keyof typeof dirtyFields]
    ) {
      return;
    }

    if (imgKey?.includes(key)) {
      if (typeof value === "string") {
        if (value.startsWith("http")) {
          formData.append(key, value);

          return;
        }
      }
      if (
        value === undefined ||
        value === null ||
        value === "" ||
        value === "null" ||
        (Array.isArray(value) && value.some((val) => val === "null"))
      ) {
        formData.append(key, "null");

        return;
      }
      if (Array.isArray(value) && !value?.length) {
        formData.append(key, "null");

        return;
      }
      if (Array.isArray(value) && value.length > 1) {
        for (let i = 0; i < value.length; i++) {
          formData.append(key, value[i]);
        }

        return;
      }
      if (value?.[0]) {
        formData.append(key, value?.[0]);

        return;
      } else if (value) {
        formData.append(key, value || null);

        return;
      }
    }

    if (Array.isArray(value)) {
      formData.append(key, JSON.stringify(value));

      return;
    } else if (value instanceof FileList) {
      for (let i = 0; i < value.length; i++) {
        formData.append(key, value[i]);
      }
    } else if (typeof value === "object" && value !== null) {
      if (value instanceof Date) {
        formData.append(key, value.toISOString());
      } else {
        formData.append(key, JSON.stringify(value));
      }
    } else {
      // if (String(value) && typeof value !== "undefined") {

      formData.append(key, String(value));

      // }
    }
  });

  return formData;
};

export const validateResponse = ({
  response,
  onSuccess,
  onError,
}: ValidateResponseProps): boolean => {
  if (response?.data && "message" in response?.data) {
    onSuccess();

    return true;
  } else {
    if (onError) {
      onError("Something went Wrong");
    }

    return false;
  }
};

/**
 * @description It filters out empty values from an object or array while retaining valid falsy values like `0` and `false`.
 * @param {T} input - Input object or array
 * @returns {FilterEmpty<T>} - Filtered object or array
 */
export const filterEmptyValues = <T>(input: T): FilterEmpty<T> => {
  if (Array.isArray(input)) {
    return input
      .map((item) => {
        // Recursively filter empty values
        if (Array.isArray(item)) {
          return filterEmptyValues(item);
        }
        if (
          item instanceof File ||
          (typeof item === "string" && item?.length > 0) ||
          item instanceof Date ||
          item instanceof Blob
        ) {
          return item; // Keep Files , strings and Dates
        }
        if (typeof item === "object" && item !== null) {
          return filterEmptyValues(item); // Recurse into objects
        }

        return item !== null && item !== undefined && item !== ""
          ? item
          : undefined; // Keep falsy but valid values like 0, false, etc.
      })
      .filter((item) => item !== undefined) as FilterEmpty<T>; // Filter out undefined
  }

  if (typeof input === "object" && input !== null) {
    return Object.entries(input)
      .map(([key, value]) => {
        // Recursively filter empty values
        if (
          value instanceof File ||
          (typeof value === "string" && value?.length > 0) ||
          value instanceof Date ||
          value instanceof Blob
        ) {
          return [key, value]; // Keep Files , strings and Dates
        }
        if (Array.isArray(value)) {
          const filteredArray = filterEmptyValues(value);

          return filteredArray.length > 0 ? [key, filteredArray] : undefined; // Include non-empty arrays
        }
        if (typeof value === "object" && value !== null) {
          const filteredObject = filterEmptyValues(value);

          return Object.keys(filteredObject).length > 0
            ? [key, filteredObject]
            : undefined; // Include non-empty objects
        }

        return value !== null && value !== undefined && value !== ""
          ? [key, value]
          : undefined; // Keep falsy but valid values
      })
      .filter((item) => item !== undefined) // Filter out undefined
      .reduce((acc, [key, value]) => {
        // @ts-expect-error - We know that key is a string
        acc[key] = value; // Construct the filtered object

        return acc;
      }, {} as FilterEmpty<T>);
  }

  return input as FilterEmpty<T>; // Return the input if it's not an object or array
};

/**
 * Recursively extracts the values of fields marked as "dirty" from the provided `values` object.
 * @template DirtyFields - An object indicating which fields are dirty.
 * @template Values - An object containing the corresponding values.
 * @param {DirtyFields} dirtyFields - Object marking dirty fields, possibly nested.
 * @param {Values} values - Object containing actual values corresponding to the dirty fields.
 * @returns {Partial<Values>} An object containing only the values of dirty fields.
 */
export function getDirtyValues<
  DirtyFields extends Record<string, unknown>,
  Values extends Record<keyof DirtyFields, unknown>,
>(dirtyFields: DirtyFields, values: Values): Partial<typeof values> {
  const dirtyValues = Object.keys(dirtyFields).reduce((prev, key) => {
    // Unsure when RFH sets this to `false`, but omit the field if so.
    if (!dirtyFields[key]) {
      return prev;
    }

    return {
      ...prev,
      [key]:
        // typeof dirtyFields[key] === "object"
        //   ? getDirtyValues(
        //       dirtyFields[key] as DirtyFields,
        //       values[key] as Values,
        //     )
        //   :
        values[key],
    };
  }, {});

  return dirtyValues;
}
